import React, { useState } from 'react';
import { Button } from '../../ui/button';
import {
  Briefcase,
  Key,
  Check,
  X,
  Lightbulb,
  Loader2,
  Trash2,
  Lock,
  Unlock,
} from 'lucide-react';

// 90s NYC themed briefcase cipher interface
// Evokes gritty urban crime dramas with a document-in-briefcase aesthetic
export const BriefcaseUI = ({
  ciphertext,
  hints,
  onSubmit,
  loading,
  result,
  attempts,
  solved,
  cipherType,
}) => {
  const [solution, setSolution] = useState('');

  // Handle keyboard input - allow full text entry
  const handleInputChange = (value) => {
    // Allow letters, spaces, and common punctuation
    const sanitized = value.toUpperCase().replace(/[^A-Z0-9 .,!?'-]/g, '');
    setSolution(sanitized);
  };

  // Clear solution
  const handleClear = () => {
    setSolution('');
  };

  // Handle submit
  const handleSubmit = () => {
    if (solution.trim()) {
      onSubmit(solution.trim());
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading && solution.trim()) {
      handleSubmit();
    }
  };

  return (
    <div className="space-y-6">
      {/* Briefcase Visual */}
      <div className="relative mx-auto max-w-md">
        {/* Briefcase body */}
        <div className="bg-gradient-to-b from-amber-950 via-amber-900 to-amber-950 rounded-lg border-4 border-amber-800 shadow-2xl p-4">
          {/* Handle */}
          <div className="flex justify-center mb-4">
            <div className="w-24 h-3 bg-gradient-to-r from-zinc-700 via-zinc-500 to-zinc-700 rounded-full border border-zinc-600" />
          </div>

          {/* Briefcase interior - document compartment */}
          <div className="bg-zinc-900 rounded-lg border-2 border-zinc-700 p-4 shadow-inner">
            {/* Lock status indicator */}
            <div
              className="flex justify-center gap-2 items-center mb-4"
              role="status"
              aria-live="polite"
              aria-label={solved ? 'Case opened' : 'Case locked'}
            >
              {solved ? (
                <Unlock className="w-5 h-5 text-green-500" aria-hidden="true" />
              ) : (
                <Lock className="w-5 h-5 text-orange-500" aria-hidden="true" />
              )}
              <div className={`w-3 h-3 rounded-full ${solved ? 'bg-green-500' : 'bg-red-500'} shadow-lg animate-pulse`} aria-hidden="true" />
              <span className="text-xs text-zinc-500 font-mono uppercase tracking-wider">
                {solved ? 'Case Opened' : 'Case Locked'}
              </span>
            </div>

            {/* Document slot visual */}
            <div className="bg-zinc-800 border border-zinc-600 rounded p-3 mb-3">
              <p className="text-xs text-zinc-500 font-mono uppercase mb-2 tracking-wider">
                Enter Decoded Message:
              </p>
              <div className="bg-black border-2 border-orange-800/50 rounded p-2 min-h-[60px] font-mono text-orange-400 text-lg tracking-wider break-all">
                {solution || <span className="text-zinc-600">_</span>}
              </div>
            </div>
          </div>

          {/* Latches */}
          <div className="flex justify-between mt-4 px-4">
            <div className="w-8 h-4 bg-gradient-to-b from-zinc-400 to-zinc-600 rounded-sm border border-zinc-500" />
            <div className="w-8 h-4 bg-gradient-to-b from-zinc-400 to-zinc-600 rounded-sm border border-zinc-500" />
          </div>
        </div>
      </div>

      {/* Ciphertext Display - styled as evidence tag */}
      <div className="bg-zinc-800 border border-zinc-700 rounded-lg overflow-hidden">
        <div className="bg-orange-600/20 border-b border-orange-600/50 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-orange-500" />
            <span className="text-xs text-orange-400 uppercase font-mono tracking-wider">Evidence Tag</span>
          </div>
          {cipherType && (
            <span className="text-xs text-zinc-500 font-mono uppercase">
              [{cipherType.replace(/_/g, ' ')}]
            </span>
          )}
        </div>
        <div className="p-4">
          <p className="font-mono text-lg text-zinc-200 tracking-widest leading-relaxed break-all">
            {ciphertext}
          </p>
        </div>
      </div>

      {/* Discovered Hints - styled as police notes */}
      {hints && hints.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-yellow-500" />
            <span className="text-sm font-mono text-yellow-400">INTEL GATHERED</span>
          </div>
          <div className="bg-zinc-900 rounded border border-zinc-700 divide-y divide-zinc-800">
            {hints.map((hint, idx) => (
              <div key={hint.id} className="p-3 flex items-start gap-2">
                <span className="text-orange-500 font-mono text-xs">#{idx + 1}</span>
                <p className="text-sm text-zinc-300 font-mono">
                  "{hint.hint_phrase}"
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Result Display */}
      {result && (
        <div
          className={`p-4 rounded-lg border transition-all duration-300 ${
            result.correct
              ? 'bg-green-900/30 border-green-600 animate-success-glow'
              : 'bg-red-900/30 border-red-600 animate-shake'
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            {result.correct ? (
              <Check className="w-6 h-6 text-green-500 animate-bounce" />
            ) : (
              <X className="w-5 h-5 text-red-500" />
            )}
            <span className={`font-mono font-bold ${result.correct ? 'text-green-400' : 'text-red-400'}`}>
              {result.correct ? 'CASE CRACKED' : 'WRONG CODE'}
            </span>
          </div>
          <p className={`text-sm font-mono ${result.correct ? 'text-green-300' : 'text-red-300'}`}>
            {result.message}
          </p>
          {result.correct && result.plaintext && (
            <p className="mt-2 text-green-200 font-mono font-bold">
              DECODED: {result.plaintext}
            </p>
          )}
          {!result.correct && result.escalation_hint && (
            <p className="mt-2 text-yellow-300 text-sm font-mono">
              TIP: {result.escalation_hint}
            </p>
          )}
        </div>
      )}

      {/* Text input */}
      <div className="space-y-3">
        <div className="flex gap-2">
          <input
            type="text"
            value={solution}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="ENTER DECODED MESSAGE..."
            disabled={loading || solved}
            className="flex-1 bg-zinc-900 border-2 border-orange-800/50 rounded px-4 py-3 text-white font-mono tracking-widest placeholder:text-zinc-600 focus:border-orange-500 focus:outline-none disabled:opacity-50"
          />
          <button
            onClick={handleClear}
            disabled={loading || solved}
            className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-zinc-400 hover:text-zinc-200 disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-zinc-500 font-mono">
          ATTEMPTS: {attempts || 0}
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <Button
          onClick={handleSubmit}
          disabled={loading || !solution.trim() || solved}
          className="flex-1 bg-orange-600 hover:bg-orange-500 text-white font-mono border border-orange-700"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              CHECKING...
            </>
          ) : (
            <>
              <Key className="w-4 h-4 mr-2" />
              OPEN CASE
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default BriefcaseUI;
